import { useCallback, useRef, useEffect } from 'react';
import { LABEL_CONFIG } from '@/constants/timeline';

export interface ResizeHandleProps {
  /** Callback with delta movement in pixels during resize */
  onResize: (delta: number) => void;
  /** Callback when resize operation completes */
  onResizeEnd: () => void;
  /** Optional additional className */
  className?: string;
}

/**
 * ResizeHandle atom - draggable vertical bar for resizing columns
 *
 * Uses pointer events API for unified mouse/touch support.
 * The handle is positioned on the right edge of the label column.
 */
export function ResizeHandle({ onResize, onResizeEnd, className = '' }: ResizeHandleProps) {
  const isDragging = useRef(false);
  const lastX = useRef(0);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    isDragging.current = true;
    lastX.current = e.clientX;

    // Capture pointer to receive events outside element
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    // Prevent text selection during drag
    e.preventDefault();
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging.current) return;

      const delta = e.clientX - lastX.current;
      lastX.current = e.clientX;
      onResize(delta);
    },
    [onResize],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (isDragging.current) {
        isDragging.current = false;
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
        onResizeEnd();
      }
    },
    [onResizeEnd],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isDragging.current = false;
    };
  }, []);

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      className={`
        group absolute top-0 right-0 z-10 h-full cursor-col-resize touch-none select-none
        ${className}
      `}
      style={{ width: LABEL_CONFIG.RESIZE_HANDLE_WIDTH }}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize label column"
    >
      {/* Visual indicator - subtle by default, visible on hover/drag */}
      <div
        className="
          absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-border-default/30 transition-colors
          group-hover:bg-border-strong/60
          group-active:bg-action-primary
        "
      />
    </div>
  );
}
